import type { BreadcrumbProps } from 'antd';
import type { NavigateFunction } from 'react-router-dom';
import { Icon } from '@/components/primitives/icon/Icon';
import { IconName } from '@/components/primitives/icon/generated/iconNames';
import type { SidebarSiteQuery } from '@/.generated/graphql';
import { updateUrlParams } from '@/components/sidebar/utils';

export type SidebarSite = NonNullable<SidebarSiteQuery['sites']['data']>[number];
export type SidebarBuilding = NonNullable<SidebarSite['buildings']>[number];
export type SidebarFloor = NonNullable<SidebarBuilding['floors']>[number];

export interface BuildBreadcrumbItemsArgs {
  sites: SidebarSite[];
  siteId: string | null | undefined;
  buildingId: string | null | undefined;
  spaceId: string | null | undefined;
  roomId: string | null | undefined;
  zoneId: string | null | undefined;
  navigate: NavigateFunction;
  pathname: string;
}

// Icons match the sidebar tree (site → building → floor) so the
// breadcrumb reads as a compact reflection of the sidebar path.
// `verticalAlign: 'middle'` on the wrapper makes the inline-flex
// span sit on the line's vertical centre — antd renders its
// dropdown chevron as a separate inline sibling that already aligns
// to the centre, so without this we'd get a baseline mismatch
// (chevron drifting visually below the icon + text).
const titleWithIcon = (iconName: IconName, name: string) => (
  <span className="inline-flex items-center gap-1" style={{ verticalAlign: 'middle' }}>
    <Icon name={iconName} size={14} />
    {name}
  </span>
);
const labelWithIcon = (iconName: IconName, name: string) => (
  <span className="inline-flex items-center gap-2" style={{ verticalAlign: 'middle' }}>
    <Icon name={iconName} size={14} />
    {name}
  </span>
);

// Cap dropdown height so big spaces (e.g. a floor with 30+ rooms)
// don't render an overflowing menu — anything past the cap scrolls.
const SIBLINGS_MAX_HEIGHT = 320;
const siblingsMenu = (
  items: NonNullable<NonNullable<BreadcrumbProps['items']>[number]['menu']>['items'],
) => ({
  items,
  style: { maxHeight: SIBLINGS_MAX_HEIGHT, overflowY: 'auto' as const },
});

const firstFloorIdInSite = (site: SidebarSite): string | undefined =>
  site.buildings?.[0]?.floors?.[0]?.id;
const firstFloorIdInBuilding = (b: SidebarBuilding): string | undefined => b.floors?.[0]?.id;

/**
 * Builds the ancestor path above the page title. Walks the sidebar's site
 * hierarchy to find the current selection, then returns antd `Breadcrumb`
 * items where each ancestor is a clickable dropdown of its siblings — pick
 * another Site / Building / Floor to jump straight to its first floor
 * without returning to the sidebar.
 *
 * Each level's dropdown lists *siblings* — items at the same level
 * excluding the one already on screen. The dropdown is dropped entirely
 * when there are no siblings, so a single-option chevron isn't presented
 * as a choice.
 */
export function buildBreadcrumbItems({
  sites,
  siteId,
  buildingId,
  spaceId,
  roomId,
  zoneId,
  navigate,
  pathname,
}: BuildBreadcrumbItemsArgs): NonNullable<BreadcrumbProps['items']> {
  const isDevicesPage = pathname === '/devices' || pathname.startsWith('/devices/');

  // "All Spaces" is the implicit root above every site. It's clickable
  // in Devices (navigates to the unscoped tables view) and a plain
  // label in Monitor / Studio (those modules don't support an
  // unscoped state — clicking would only confuse).
  const allSpacesItem = isDevicesPage
    ? { title: 'All Spaces', onClick: () => updateUrlParams(navigate, {}) }
    : { title: 'All Spaces' };

  const items: NonNullable<BreadcrumbProps['items']> = [allSpacesItem];

  // A floor counts as the path target if EITHER the URL points at it
  // directly (`spaceId`) OR a deep link points at one of its rooms /
  // zones without `spaceId`. The room/zone branches let breadcrumbs
  // render correctly for `?roomId=…` / `?zoneId=…` URLs that originate
  // outside the sidebar (deep links, shared URLs).
  const floorMatches = (f: {
    id: string;
    rooms?: { id: string }[] | null;
    zones?: { id: string }[] | null;
  }) =>
    Boolean(
      (spaceId && f.id === spaceId) ||
        (roomId && (f.rooms ?? []).some((r) => r.id === roomId)) ||
        (zoneId && (f.zones ?? []).some((z) => z.id === zoneId)),
    );

  for (const site of sites) {
    const matchesSite = siteId && site.id === siteId;
    const matchesBuildingInSite = (site.buildings ?? []).some(
      (b) => (buildingId && b.id === buildingId) || (b.floors ?? []).some(floorMatches),
    );
    if (!matchesSite && !matchesBuildingInSite) continue;

    const siteSiblings = sites.filter((s) => s.id !== site.id);
    const siteSiblingsMenu =
      siteSiblings.length > 0
        ? siblingsMenu(
            siteSiblings.map((s) => ({
              key: s.id,
              label: labelWithIcon('icon-siteoutlined', s.name),
              onClick: () => {
                const floorId = firstFloorIdInSite(s);
                if (floorId) updateUrlParams(navigate, { floorId });
              },
            })),
          )
        : undefined;

    items.push({
      title: titleWithIcon('icon-siteoutlined', site.name),
      ...(siteSiblingsMenu && { menu: siteSiblingsMenu }),
    });
    if (matchesSite) return items;

    const buildings = site.buildings ?? [];
    for (const b of buildings) {
      const matchesBuilding = buildingId && b.id === buildingId;
      const matchesFloorInBuilding = (b.floors ?? []).some(floorMatches);
      if (!matchesBuilding && !matchesFloorInBuilding) continue;

      const buildingSiblings = buildings.filter((b2) => b2.id !== b.id);
      const buildingSiblingsMenu =
        buildingSiblings.length > 0
          ? siblingsMenu(
              buildingSiblings.map((b2) => ({
                key: b2.id,
                label: labelWithIcon('building', b2.name),
                onClick: () => {
                  const floorId = firstFloorIdInBuilding(b2);
                  if (floorId) updateUrlParams(navigate, { floorId });
                },
              })),
            )
          : undefined;

      items.push({
        title: titleWithIcon('building', b.name),
        ...(buildingSiblingsMenu && { menu: buildingSiblingsMenu }),
      });
      if (matchesBuilding) return items;

      const floors = b.floors ?? [];
      for (const f of floors) {
        if (floorMatches(f)) {
          const floorSiblings = floors.filter((f2) => f2.id !== f.id);
          const floorSiblingsMenu =
            floorSiblings.length > 0
              ? siblingsMenu(
                  floorSiblings.map((f2) => ({
                    key: f2.id,
                    label: labelWithIcon('layer', f2.name),
                    onClick: () => updateUrlParams(navigate, { floorId: f2.id }),
                  })),
                )
              : undefined;

          items.push({
            title: titleWithIcon('layer', f.name),
            ...(floorSiblingsMenu && { menu: floorSiblingsMenu }),
          });
          // If a Room or Zone is also selected, append it as the leaf
          // breadcrumb item so the path matches the sidebar's deepest
          // selectable level (Monitor: rooms / zones).
          if (roomId) {
            const rooms = f.rooms ?? [];
            const room = rooms.find((r) => r.id === roomId);
            if (room) {
              const roomSiblings = rooms.filter((r) => r.id !== roomId);
              const roomSiblingsMenu =
                roomSiblings.length > 0
                  ? siblingsMenu(
                      roomSiblings.map((r) => ({
                        key: r.id,
                        label: labelWithIcon('room', r.name),
                        onClick: () => updateUrlParams(navigate, { floorId: f.id, roomId: r.id }),
                      })),
                    )
                  : undefined;
              items.push({
                title: titleWithIcon('room', room.name),
                ...(roomSiblingsMenu && { menu: roomSiblingsMenu }),
              });
            }
          } else if (zoneId) {
            const zones = f.zones ?? [];
            const zone = zones.find((z) => z.id === zoneId);
            if (zone) {
              const zoneSiblings = zones.filter((z) => z.id !== zoneId);
              const zoneSiblingsMenu =
                zoneSiblings.length > 0
                  ? siblingsMenu(
                      zoneSiblings.map((z) => ({
                        key: z.id,
                        label: labelWithIcon('zone', z.name),
                        onClick: () => updateUrlParams(navigate, { floorId: f.id, zoneId: z.id }),
                      })),
                    )
                  : undefined;
              items.push({
                title: titleWithIcon('zone', zone.name),
                ...(zoneSiblingsMenu && { menu: zoneSiblingsMenu }),
              });
            }
          }
          return items;
        }
      }
    }
  }
  return items;
}
